import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PaymentModal from "../components/PaymentModal";
import { addPurchasePayment, getPurchaseById, updatePurchase, markAsPaid, sendPrintRequest, getBranchTaxes } from "../api"; // Replace with your API function
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import InlineLoading from "../components/InlineLoading";
import { getPurchaseItems } from "../utils/purchaseItems";
import { notify } from '../utils/alertBus';
import { getPrintMode, PRINT_MODE_REMOTE } from '../utils/printSettings';
import {
  buildReceiptItemDisplayName as buildItemDisplayName,
  buildReceiptItemLine,
  formatVoucherQuantity,
} from '../utils/receiptFormatting';

const PurchaseReceiptPage = ({ previewOnly = false }) => {
  const { purchaseId } = useParams(); // Get the purchase ID from the URL
  const [purchase, setPurchase] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const pdfRef = useRef(null);
  const pdfIframeRef = useRef(null);
  const autoPrintDoneRef = useRef(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [receiptWidth, setReceiptWidth] = useState("80"); // Default to 80mm
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const location = useLocation();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();
  const canReadSales = hasPermission(user, "sales.read");
  const canUpdateSale = hasPermission(user, "sales.update");
  const canReadClient = hasPermission(user, "client.read");
  const canCreateClient = hasPermission(user, "client.create");
  const canReadTaxes = hasPermission(user, "tax.read") || hasPermission(user, "tax.exclude");
  const autoPrintParam = new URLSearchParams(location.search).get("autoprint");
  const shouldAutoPrint = autoPrintParam === "1" || autoPrintParam === "true";

  useEffect(() => {
    autoPrintDoneRef.current = false;
  }, [purchaseId, autoPrintParam]);

  useEffect(() => {
    setIsPdfLoaded(false);
  }, [pdfUrl]);

  const buildBarcodeDataUrl = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return null;
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, normalized, {
        format: "CODE128",
        displayValue: false,
        height: 40,
        margin: 0,
      });
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.warn("No se pudo generar el barcode:", error);
      return null;
    }
  };

  const navigate = useNavigate();

  const normalizePurchasePayload = (data) => {
    if (!data) return data;
    const normalized = { ...data };
    if (typeof normalized.detailForPayment === "string") {
      try {
        normalized.detailForPayment = JSON.parse(normalized.detailForPayment);
      } catch (error) {
        console.warn("No se pudo parsear detailForPayment:", error);
        normalized.detailForPayment = {};
      }
    }
    return normalized;
  };

  const refreshPurchaseData = async ({ autoOpenPayment = false } = {}) => {
    const raw = await getPurchaseById(branchId, purchaseId);
    const data = normalizePurchasePayload(raw);
    setPurchase(data);
    generatePDF(data);
    const paidStatus = data.paymentStatus === "paid" || data.isPaid;
    setIsPaid(paidStatus);
    if (autoOpenPayment) {
      const isSuspended = data.saleStatus === "suspended";
      setIsPaymentModalOpen(!previewOnly && !paidStatus && canUpdateSale && !isSuspended);
    }
    return data;
  };

  useEffect(() => {
    const fetchPurchase = async () => {
      try {
        await refreshPurchaseData({ autoOpenPayment: true });
      } catch (error) {
        console.error("Error fetching purchase:", error);
      }
    };

    fetchPurchase();
  }, [purchaseId, branchId, canUpdateSale, previewOnly]);

  useEffect(() => {
    if (!branchId || !canReadTaxes) {
      setTaxes([]);
      return;
    }
    let isMounted = true;
    const loadTaxes = async () => {
      try {
        const data = await getBranchTaxes(branchId);
        if (!isMounted) return;
        setTaxes(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching taxes:", error);
        setTaxes([]);
      }
    };
    loadTaxes();
    return () => {
      isMounted = false;
    };
  }, [branchId, canReadTaxes]);

  const handleConfirmPayment = async (
    method,
    details,
    paymentTerm,
    nextDocumentType,
    nextClientId
  ) => {
    try {
      if (!canUpdateSale) return;
      const totalAmount = Number(purchase?.totalAmount || 0);
      const currentPaidAmount = Number(purchase?.paidAmount || 0);
      const currentBalance =
        purchase?.balance !== undefined
          ? Number(purchase.balance || 0)
          : Math.max(totalAmount - currentPaidAmount, 0);
      const isCredit = paymentTerm === "credit";
      const isExistingCredit = purchase?.paymentTerm === "credit";
      const initialPayment = Number(details?.initialPayment || 0);
      let paymentAmount = 0;
      let paymentMethod = method;
      const paymentPayload = {};
      const resolvedDocumentType =
        nextDocumentType === "boleta" || nextDocumentType === "factura"
          ? nextDocumentType
          : null;

      if (isCredit) {
        paymentMethod = details?.initialPaymentMethod || "efectivo";
        paymentAmount = Math.min(Math.max(initialPayment, 0), currentBalance);
        paymentPayload.paymentTerm = "credit";
        paymentPayload.creditDueDate = details?.creditDueDate || null;
        paymentPayload.initialPayment = paymentAmount;
        paymentPayload.paymentMethod = null;
        paymentPayload.isPaid = currentPaidAmount + paymentAmount >= totalAmount && totalAmount > 0;
        paymentPayload.detailForPayment = {
          ...details,
          creditDueDate: details?.creditDueDate || null,
        };
      } else {
        if (method === "efectivo") {
          const received = Number(details?.receivedAmount || 0);
          paymentAmount = Math.min(received, currentBalance);
        } else {
          paymentAmount = currentBalance;
        }
        paymentPayload.paymentTerm = isExistingCredit ? "credit" : "immediate";
        paymentPayload.paymentMethod = method;
        paymentPayload.isPaid = currentPaidAmount + paymentAmount >= totalAmount && totalAmount > 0;
        paymentPayload.detailForPayment = details;
      }

      if (resolvedDocumentType) {
        paymentPayload.documentType = resolvedDocumentType;
      }
      if (purchase?.saleStatus === "open") {
        paymentPayload.saleStatus = "completed";
      }
      if (Number.isInteger(nextClientId)) {
        paymentPayload.clientId = nextClientId;
      }

      const response = await updatePurchase(branchId, purchaseId, paymentPayload);
      if (response?.warning) {
        notify(response.warning);
      }

      if (paymentAmount > 0 && paymentMethod) {
        const reference =
          method === "transferencia" || method === "tarjeta"
            ? details?.transactionNumber
            : method === "cheque"
              ? details?.chequeNumber
              : undefined;
        const note =
          method === "cheque" && details?.chequeExpiration
            ? `Expira: ${details.chequeExpiration}`
            : undefined;
        await addPurchasePayment(branchId, purchaseId, {
          amount: paymentAmount,
          method: paymentMethod,
          reference,
          note,
        });
      }

      await refreshPurchaseData({ autoOpenPayment: false });
      setIsPaymentModalOpen(false);
    } catch (error) {
      console.error("Error al registrar el pago:", error);
      notify("Error al registrar el pago.");
    }
  };

  useEffect(() => {
    if (purchase) {
      generatePDF(purchase);
    }
  }, [receiptWidth, taxes, purchase]);

  const generatePDF = (purchase) => {
    if (!purchase) return;

    const purchaseItems = getPurchaseItems(purchase);
    const isSuspended = purchase.saleStatus === "suspended";
    const suspendedCode = isSuspended ? purchase.suspendedCode : null;
    const excludedTaxIds = Array.isArray(purchase.excludedTaxIds) ? purchase.excludedTaxIds : [];
    const taxNameById = new Map(
      (taxes || []).map((tax) => [Number(tax.id), tax.name || `Impuesto ${tax.id}`])
    );
    const excludedLabels = excludedTaxIds
      .map((id) => taxNameById.get(Number(id)) || `#${id}`)
      .filter(Boolean);
    const excludedNote =
      excludedLabels.length > 0 ? `Impuestos excluidos: ${excludedLabels.join(", ")}` : null;
    const breakdown = Array.isArray(purchase.taxBreakdown) ? purchase.taxBreakdown : [];
    const grossSubtotal = purchaseItems.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      if (qty <= 0) return sum;
      const price = Number(item?.price || 0);
      return sum + Math.floor(price * qty);
    }, 0);
    const itemDiscountAmount = purchaseItems.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      if (qty <= 0) return sum;
      const discountPerUnit = Number(item?.discount || 0);
      return sum + Math.floor(discountPerUnit * qty);
    }, 0);
    const subtotalAfterItemDiscount =
      typeof purchase.itemsSubtotal === "number"
        ? purchase.itemsSubtotal
        : Math.max(grossSubtotal - itemDiscountAmount, 0);
    const discountPercentNum = Number(purchase.orderDiscountPercentage);
    const discountOrderPercent = Number.isFinite(discountPercentNum)
      ? discountPercentNum
      : null;
    const discountAmountNum = Number(purchase.orderDiscountAmount);
    const discountOrderAmount =
      discountOrderPercent !== null
        ? Math.floor(subtotalAfterItemDiscount * (discountOrderPercent / 100))
        : Number.isFinite(discountAmountNum)
          ? discountAmountNum
          : 0;
    const totalDiscountAmount = itemDiscountAmount + Math.max(discountOrderAmount, 0);
    const taxTotal = Number.isFinite(Number(purchase.taxTotal))
      ? Number(purchase.taxTotal)
      : breakdown.reduce((sum, row) => sum + (Number(row?.amount || 0)), 0);
    const totalAmount = Number(purchase.totalAmount || 0);
    const netTotal = Number.isFinite(Number(purchase.netTotal))
      ? Number(purchase.netTotal)
      : Math.max(totalAmount - taxTotal, 0);
    const docWidth = receiptWidth - 10;
    const textAreaWidth = docWidth - 28; // leave room for right-aligned numbers
    const marginTop = 5;
    const lineHeight = 5;
    let estimatedHeight = marginTop + 30;

    estimatedHeight += purchase.Client ? 35 : 10;
    estimatedHeight += 10;

    purchaseItems.forEach((item) => {
      if (item.quantity <= 0) return;
      estimatedHeight += lineHeight * 3; // base line + net/total line
      if (item.discount && item.discount > 0) {
        estimatedHeight += lineHeight; // extra line for discount
      }
    });

    estimatedHeight += lineHeight; // Subtotal
    if (totalDiscountAmount > 0) estimatedHeight += lineHeight;
    estimatedHeight += lineHeight; // Neto
    estimatedHeight += lineHeight; // Impuestos total
    if (breakdown.length) estimatedHeight += breakdown.length * lineHeight;
    estimatedHeight += lineHeight; // Total
    if (excludedNote) {
      estimatedHeight += Math.ceil(excludedNote.length / 28) * lineHeight;
    }

    if (isSuspended && suspendedCode) {
      estimatedHeight += receiptWidth < 80 ? 22 : 28;
    }

    estimatedHeight += 30;
    const pageHeight = receiptWidth < 80 ? Math.max(estimatedHeight * 1.5, 80) : Math.max(estimatedHeight, 80);
    let currentY = marginTop;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [docWidth, pageHeight],
    });

    doc.setFontSize(10);
    doc.text(isSuspended ? "Venta Suspendida" : "Comprobante de Venta", docWidth / 2, currentY, { align: "center" });
    currentY += lineHeight + 2;

    doc.setFontSize(7);
    doc.text("Nro Venta: " + purchase.id, 5, currentY);
    if (receiptWidth === 58) currentY += lineHeight;
    doc.text("Fecha: " + new Date(purchase.createdAt).toLocaleString(), docWidth - 5, currentY, { align: "right" });
    currentY += lineHeight + 2;

    if (isSuspended && suspendedCode) {
      doc.setFontSize(7);
      doc.text("Voucher para reanudar venta", docWidth / 2, currentY, { align: "center" });
      currentY += lineHeight + 1;
      const barcodeDataUrl = buildBarcodeDataUrl(suspendedCode);
      if (barcodeDataUrl) {
        const barcodeWidth = Math.max(docWidth - 12, 40);
        const barcodeHeight = receiptWidth < 80 ? 14 : 18;
        const barcodeX = (docWidth - barcodeWidth) / 2;
        doc.addImage(barcodeDataUrl, "PNG", barcodeX, currentY, barcodeWidth, barcodeHeight);
        currentY += barcodeHeight + 2;
        doc.text(suspendedCode, docWidth / 2, currentY, { align: "center" });
        currentY += lineHeight + 1;
      }
    }

    if (purchase.Client) {
      const { name, rut, email, address, city, phone } = purchase.Client;
      doc.setFontSize(receiptWidth < 80 ? 6 : 8);
      doc.text(`Nombre: ${name || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`RUT: ${rut || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Email: ${email || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Dirección: ${address || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Ciudad: ${city || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Teléfono: ${phone || "N/A"}`, 5, currentY); currentY += lineHeight;
    } else {
      doc.setFontSize(8);
      doc.text("Venta Detalle (sin cliente)", 5, currentY); currentY += lineHeight;
    }

    doc.setFontSize(8);
    doc.text("Detalle de la Venta:", 5, currentY); currentY += lineHeight + 2;

    purchaseItems.forEach((item) => {
      if (item.quantity <= 0) return;

      const hasDiscount = item.discount && item.discount > 0;
      const originalPrice = Number(item.price) || 0;
      const discountPerUnit = Number(item.discount || 0);
      const quantityLabel = formatVoucherQuantity(item.quantity);
      const lineGrossTotal = Math.floor(originalPrice * Number(item.quantity || 0));
      const totalDiscount = Math.floor(discountPerUnit * Number(item.quantity || 0));
      const percentage =
        hasDiscount && originalPrice > 0
          ? Math.round((discountPerUnit / originalPrice) * 100)
          : null;

      // 🧾 Line 1: Product at full price
      const line = buildReceiptItemLine({
        item,
        unitPrice: originalPrice,
        quantityLabel,
      });
      const subtotal = `$${lineGrossTotal.toLocaleString("de-DE")}`;

      // Break line into chunks of ~25 characters (you can adjust the width in mm)
      const wrappedLines = doc.splitTextToSize(line, textAreaWidth);
      wrappedLines.forEach((part, idx) => {
        const yOffset = currentY + idx * lineHeight;
        doc.text(part, 6, yOffset);
        if (idx === wrappedLines.length - 1) {
          doc.text(subtotal, docWidth - 5 - doc.getTextWidth(subtotal), yOffset);
        }
      });
      currentY += wrappedLines.length * lineHeight;

      // 🧾 Line 2: Discount (aligned)
      if (hasDiscount) {
        doc.setFontSize(7);
        const discountDesc = `Descuento${percentage !== null ? ` (${percentage}%)` : ""}`;
        const discountTotal = `- $${totalDiscount.toLocaleString("de-DE")}`;
        const discountLines = doc.splitTextToSize(discountDesc, textAreaWidth);
        discountLines.forEach((part, idx) => {
          const yOffset = currentY + idx * lineHeight;
          doc.text(part, 6, yOffset);
          if (idx === discountLines.length - 1) {
            doc.text(discountTotal, docWidth - 5 - doc.getTextWidth(discountTotal), yOffset);
          }
        });
        currentY += discountLines.length * lineHeight;
        doc.setFontSize(8);
      }
    });

    currentY += 2;
    doc.setFontSize(9);

    const subtotalText = `Subtotal: $${grossSubtotal.toLocaleString("de-DE")}`;
    doc.text(subtotalText, docWidth - 10 - doc.getTextWidth(subtotalText), currentY);
    currentY += lineHeight;

    if (totalDiscountAmount > 0) {
      const discountLabel = `Descuentos${
        discountOrderPercent != null ? ` (orden ${discountOrderPercent}%)` : ""
      }: -$${totalDiscountAmount.toLocaleString("de-DE")}`;
      doc.text(discountLabel, docWidth - 10 - doc.getTextWidth(discountLabel), currentY);
      currentY += lineHeight;
    }

    const netText = `Valor neto: $${netTotal.toLocaleString("de-DE")}`;
    doc.text(netText, docWidth - 10 - doc.getTextWidth(netText), currentY);
    currentY += lineHeight;

    const taxesText = `Impuestos: $${taxTotal.toLocaleString("de-DE")}`;
    doc.text(taxesText, docWidth - 10 - doc.getTextWidth(taxesText), currentY);
    currentY += lineHeight;

    if (breakdown.length) {
      breakdown.forEach((row) => {
        const label = `- ${row.name || "Impuesto"}${row.rate ? ` (${row.rate}%)` : ""}: $${Number(row.amount || 0).toLocaleString("de-DE")}`;
        doc.text(label, docWidth - 10 - doc.getTextWidth(label), currentY);
        currentY += lineHeight;
      });
    }

    if (excludedNote) {
      const noteLines = doc.splitTextToSize(excludedNote, textAreaWidth);
      noteLines.forEach((part, idx) => {
        const yOffset = currentY + idx * lineHeight;
        doc.text(part, 6, yOffset);
      });
      currentY += noteLines.length * lineHeight;
    }

    const totalText = `Total: $${totalAmount.toLocaleString("de-DE")}`;
    doc.text(totalText, docWidth - 10 - doc.getTextWidth(totalText), currentY);
    currentY += lineHeight + 1;

    const method = purchase.paymentMethod || "No especificado";
    const detail = purchase.detailForPayment || {};
    const paymentTerm =
      purchase.paymentTerm === "credit" || method === "crédito"
        ? "credit"
        : "immediate";
    const paidAmount = Number(purchase.paidAmount || 0);
    const balance =
      purchase.balance !== undefined
        ? Number(purchase.balance || 0)
        : Math.max((Number(purchase.totalAmount || 0) - paidAmount), 0);
    const formatDate = (value) => {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
    };

    doc.setFontSize(8);
    doc.text("Detalles del Pago:", 5, currentY); currentY += lineHeight;
    doc.text(`Condición: ${paymentTerm === "credit" ? "Crédito" : "Contado"}`, 5, currentY); currentY += lineHeight;
    if (paymentTerm === "immediate") {
      doc.text(`Método: ${method}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "immediate" && method === "cheque") {
      doc.text(`Cheque N°: ${detail.chequeNumber || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Expira: ${detail.chequeExpiration || "N/A"}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "immediate" && ["transferencia", "tarjeta"].includes(method)) {
      doc.text(`Transacción: ${detail.transactionNumber || "N/A"}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "immediate" && method === "efectivo") {
      const received = parseInt(detail.receivedAmount || 0);
      const change = Math.max(received - totalAmount, 0);
      doc.text(`Recibido: $${received.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
      doc.text(`Vuelto: $${change.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "credit") {
      const dueDate = formatDate(purchase.creditDueDate || detail.creditDueDate || detail.creditExpiration);
      doc.text(`Vence: ${dueDate}`, 5, currentY); currentY += lineHeight;
      const initialPayment = Number(purchase.initialPayment || detail.initialPayment || 0);
      if (initialPayment > 0) {
        doc.text(`Abono Inicial: $${initialPayment.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
      }
    }

    if (paidAmount > 0 || balance > 0) {
      doc.text(`Pagado: $${paidAmount.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
      doc.text(`Pendiente: $${balance.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
    }

    currentY += 4;
    doc.setFontSize(10);
    doc.text("ChamaFruit", docWidth / 2, currentY, { align: "center" });

    pdfRef.current = doc;
    const pdfBlob = doc.output("blob");
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);
    setPdfUrl(pdfBlobUrl);
  };

  const triggerLocalPrint = () => {
    if (!pdfUrl) return false;
    const iframeWindow = isPdfLoaded ? pdfIframeRef.current?.contentWindow : null;
    if (iframeWindow) {
      iframeWindow.focus();
      iframeWindow.print();
      return true;
    }
    const printWindow = window.open(pdfUrl);
    if (printWindow) {
      printWindow.addEventListener("load", () => {
        printWindow.focus();
        printWindow.print();
      });
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!shouldAutoPrint || !pdfUrl || !isPdfLoaded || autoPrintDoneRef.current) return;
    autoPrintDoneRef.current = true;
    triggerLocalPrint();
  }, [shouldAutoPrint, pdfUrl, isPdfLoaded]);

  const handlePrint = async () => {
    try {
      if (!canReadSales && !canUpdateSale) return;
      const isSuspended = purchase?.saleStatus === "suspended";
      if (isSuspended) {
        if (!triggerLocalPrint()) {
          notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
        }
        return;
      }
      const shouldMarkPaid =
        !previewOnly && !isSuspended && !isPaid && purchase?.paymentTerm !== "credit";
      if (shouldMarkPaid && !canUpdateSale) return;
      if (shouldMarkPaid) {
        // Ensure the purchase is marked as paid before printing (non-credit only)
        await markAsPaid(branchId, purchaseId);
        setIsPaid(true);
      }

      const printMode = getPrintMode();
      const useRemotePrint = printMode === PRINT_MODE_REMOTE;
      if (useRemotePrint) {
        sendPrintRequest(branchId, purchaseId);
      } else if (!triggerLocalPrint()) {
        notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
        return;
      }

      const notification = document.createElement("div");
      notification.textContent = useRemotePrint
        ? "Imprimiendo comprobante..."
        : "Abriendo comprobante en el navegador...";
      notification.style.position = "fixed";
      notification.style.bottom = "20px";
      notification.style.right = "20px";
      notification.style.backgroundColor = "#4caf50";
      notification.style.color = "white";
      notification.style.padding = "10px";
      notification.style.borderRadius = "5px";
      notification.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.1)";
      document.body.appendChild(notification);

      setTimeout(() => {
        document.body.removeChild(notification);
        if (!previewOnly) {
          navigate(`/branches/${branchId}/purchases/new`);
        }
      }, 3000);
    } catch (error) {
      console.error("Error updating purchase as paid:", error);
      notify("Error al actualizar la compra antes de imprimir.");
    }
  };

  const isSuspended = purchase?.saleStatus === "suspended";
  const saleStatus = String(purchase?.saleStatus || "").toLowerCase();
  const isCancelled = Boolean(purchase?.isDeleted) || saleStatus === "cancelled";
  const isClosedSale =
    saleStatus === "completed"
    || saleStatus === "closed"
    || (saleStatus !== "" && saleStatus !== "open" && saleStatus !== "suspended" && saleStatus !== "cancelled");
  const paidAmount = Number(purchase?.paidAmount || 0);
  const totalAmount = Number(purchase?.totalAmount || 0);
  const pendingBalance =
    purchase?.balance !== undefined
      ? Number(purchase.balance || 0)
      : Math.max(totalAmount - paidAmount, 0);
  const hasMissingPayment =
    pendingBalance > 0
    || purchase?.paymentStatus === "partial"
    || (!isPaid && totalAmount > 0);
  const canAddPayment =
    !previewOnly
    && canUpdateSale
    && !isSuspended
    && !isCancelled
    && isClosedSale
    && hasMissingPayment;
  const canPrint = previewOnly || isSuspended || isPaid || purchase?.paymentTerm === "credit";
  const title = previewOnly && !isSuspended ? "Comprobante Provisorio" : (isSuspended ? "Venta Suspendida" : "Comprobante de Venta");

  return (

    <>
      <div className="flex flex-col items-center p-6">
        <h1 className="text-lg font-bold mb-2 text-center">
          {title}
        </h1>
        {previewOnly && !isPaid && !isSuspended && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded mb-2">
            Vista previa: pago pendiente.
          </p>
        )}

        <div className="mb-4 flex items-center gap-2">
          <label htmlFor="width" className="text-sm font-semibold">Tamaño del recibo:</label>
          <select
            id="width"
            value={receiptWidth}
            onChange={(e) => {
              setReceiptWidth(e.target.value);
              if (purchase) generatePDF(purchase, e.target.value); // Regenerate on change
            }}
            className="border p-1 rounded text-sm"
          >
            <option selected value="80">80 mm</option>
            <option value="58">58 mm</option>
          </select>
        </div>

        {/* Mobile-Friendly PDF View */}
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="w-full max-w-[400px] h-[500px] border border-gray-300 shadow-lg"
            title="Receipt"
            ref={pdfIframeRef}
            onLoad={() => setIsPdfLoaded(true)}
          ></iframe>
        ) : (
          <p>
            <InlineLoading label="Cargando comprobante" />
          </p>
        )}

        {canAddPayment && (
          <button
            onClick={() => setIsPaymentModalOpen(true)}
            className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded text-sm w-full max-w-[300px]"
          >
            Agregar pago
            {pendingBalance > 0 ? ` ($${pendingBalance.toLocaleString("de-DE")} pendiente)` : ""}
          </button>
        )}

        {/* Print Button */}
        {(canReadSales || canUpdateSale) && (
          <button
            onClick={handlePrint}
            className={`mt-2 px-4 py-2 text-white rounded text-sm w-full max-w-[300px] ${canPrint ? 'bg-blue-500 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
            disabled={!canPrint}
          >
            Imprimir Recibo
          </button>
        )}
        {/* Download PDF Button */}
        {(canReadSales || canUpdateSale) && (
          <button
            onClick={() => {
              if (pdfRef.current && purchase) {
                const clientName = purchase.Client?.name?.replace(/\s+/g, "_") || "detalle";
                const fileName = `venta_${clientName}_${purchaseId}.pdf`;
                pdfRef.current.save(fileName);
              }
            }}
            className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm w-full max-w-[300px]"
          >
            Descargar PDF
          </button>
        )}
      </div>
      {!previewOnly && (
        <PaymentModal
          isOpen={isPaymentModalOpen && canUpdateSale}
          purchaseData={purchase}
          onClose={() => setIsPaymentModalOpen(false)}
          onConfirmPayment={handleConfirmPayment}
          canReadClient={canReadClient}
          canCreateClient={canCreateClient}
        />
      )}
    </>
  );
};

export default PurchaseReceiptPage;
